import {
  ApiChartAttribute,
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
} from "@incendium/api";
import { Box, Stack } from "@mui/material";
import StandardAnalyticsCard from "features/analytics/components/StandardAnalyticsCard";
import {
  CHART_KEY_DASHBOARD_LANDING_PIE,
  CHART_KEY_DASHBOARD_LANDING_TABLE,
  CHART_KEY_DASHBOARD_LANDING_TREND,
} from "features/analytics/constants/chartKeys";
import { table5Rows } from "features/analytics/constants/constants";
import { useMemo, useState } from "react";
import {
  dashboardChartKey,
  useIsEcommerceProject,
  withoutAddToCartMetrics,
} from "features/dashboard/utils/ecommerceMetrics";

interface ILandingPagesTabProps {
  attributes: ApiChartAttribute[];
}

function LandingPagesTab({ attributes }: ILandingPagesTabProps) {
  const isEcommerce = useIsEcommerceProject();
  const [metric, setMetric] = useState<ApiMetric>(
    ApiMetric.METRIC_SESSIONS_COUNT,
  );

  const tableFields = useMemo(
    () =>
      withoutAddToCartMetrics(
        [
          ApiMetric.METRIC_SESSIONS_COUNT,
          ApiMetric.METRIC_EFFECTIVE_SESSIONS_COUNT,
          ApiMetric.METRIC_EFFECTIVE_SESSION_RATE,
          ApiMetric.METRIC_SESSION_ADD_TO_CART_CLICK_RATE,
          ApiMetric.METRIC_CHECKOUT_START_RATE,
          ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT,
          ApiMetric.METRIC_MACRO_CONVERSION_RATE,
          ApiMetric.METRIC_REVENUE,
        ],
        isEcommerce,
      ),
    [isEcommerce],
  );

  return (
    <>
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        sx={{ flexShrink: 0, width: "100%", minWidth: 0 }}
      >
        <Box
          sx={{
            height: { xs: 320, md: 400 },
            flex: 1,
            minWidth: 0,
            width: { xs: "100%", md: "66.666%" },
          }}
        >
          <StandardAnalyticsCard
            chartKey={CHART_KEY_DASHBOARD_LANDING_TREND}
            staticAttributes={attributes}
            onChangeMetric={setMetric}
            chart={{
              name: "Top 5 Landing Locations by",
              dimension: [
                ApiDimension.DIMENSION_SESSION_START_BY_DAY,
                ApiDimension.DIMENSION_LANDING_PAGE_LOCATION,
              ],
              template: ApiChartTemplate.TREND,
              type: ApiChartType.GRAPH,
              attributes: [],
              limit: 5,
              yAxisKeys: [
                {
                  key: "l",
                  fields: [ApiMetric.METRIC_SESSIONS_COUNT],
                },
              ],
              displayOptions: {
                noFilters: true,
                noAttributionToggle: true,
                availableMetrics: [
                  ApiMetric.METRIC_SESSIONS_COUNT,
                  ApiMetric.METRIC_SESSION_SALES_COUNT,
                  ApiMetric.METRIC_EFFECTIVE_SESSIONS_COUNT,
                  ApiMetric.METRIC_EFFECTIVE_PAGEVIEWS_COUNT,
                  ApiMetric.METRIC_MACRO_CONVERSIONS_COUNT,
                ],
              },
            }}
          />
        </Box>

        <Box
          sx={{
            height: { xs: 320, md: 400 },
            width: { xs: "100%", md: "33.333%" },
            flexShrink: 0,
            minWidth: 0,
          }}
        >
          <StandardAnalyticsCard
            chartKey={CHART_KEY_DASHBOARD_LANDING_PIE}
            staticAttributes={attributes}
            chart={{
              name: "Landing Page Composition",
              dimension: [ApiDimension.DIMENSION_LANDING_PAGE_LOCATION],
              template: ApiChartTemplate.SNAPSHOT,
              type: ApiChartType.PIE,
              attributes: [],
              limit: 15,
              yAxisKeys: [
                {
                  key: "l",
                  fields: [metric],
                },
              ],
              displayOptions: {
                noFilters: true,
                noAttributionToggle: true,
                showTotals: true,
              },
            }}
          />
        </Box>
      </Stack>

      <Box
        sx={{
          height: table5Rows,
          flexShrink: 0,
          width: "100%",
          minWidth: 0,
          overflowX: "auto",
        }}
      >
        <StandardAnalyticsCard
          chartKey={dashboardChartKey(
            CHART_KEY_DASHBOARD_LANDING_TABLE,
            isEcommerce,
          )}
          staticAttributes={attributes}
          chart={{
            name: "Landing Page Performance",
            dimension: [ApiDimension.DIMENSION_LANDING_PAGE_LOCATION],
            template: ApiChartTemplate.TABLE,
            type: ApiChartType.TABLE,
            attributes: [],
            orderBy: `${ApiMetric.METRIC_SESSIONS_COUNT} desc`,
            yAxisKeys: [
              {
                key: "l",
                fields: tableFields,
              },
            ],
            displayOptions: {
              noFilters: true,
              noAttributionToggle: true,
              rowsPerPage: 0,
              showTotals: true,
            },
          }}
        />
      </Box>
    </>
  );
}

export default LandingPagesTab;
